import { formatPlainDate, toShortTime } from '../../core/dates/plain-date';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalAppointment } from '../../core/models/portal-appointment';
import { Badge } from '../../ui/badge';
import { CalendarIcon } from '../../ui/portal-icons';
import { APPOINTMENT_STATUS_KEYS } from './appointment-status-keys';
import { APPOINTMENT_STATUS_TONES } from './appointment-status-tones';

/**
 * The one meeting still ahead, in the case header. "When do I next see somebody" is the question
 * after "what stage am I at", and it used to be answered only at the foot of the page.
 *
 * The weekday is printed because a customer plans around "Thursday", not "18 September".
 */
export function CaseNextAppointment({ appointment }: { appointment: PortalAppointment }) {
    const { t, language } = useTranslation();

    const service = language === 'kk' ? appointment.serviceNameKk : appointment.serviceNameRu;
    const online = appointment.meetingFormat === 'Online';
    const place = online
        ? t('portal.format.online')
        : [t('portal.format.in_person'), appointment.city].filter((part) => part !== null).join(' · ');
    const when =
        appointment.scheduledDate === null || appointment.scheduledTime === null
            ? t('portal.appointment_no_time')
            : `${formatPlainDate(appointment.scheduledDate, language, { weekday: 'short', day: 'numeric', month: 'long' })} · ${toShortTime(appointment.scheduledTime)}`;

    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200/80 bg-slate-50/70 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
            <span
                aria-hidden="true"
                className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-brand-600 ring-1 ring-slate-200/80 dark:bg-slate-900 dark:text-brand-400 dark:ring-slate-800"
            >
                <CalendarIcon />
            </span>

            <div className="min-w-0 flex-1 basis-48">
                <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
                    {t('portal.next_appointment')}
                </p>
                <p className="font-semibold text-slate-900 tabular-nums dark:text-slate-100">{when}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                    {service === null ? place : `${service} · ${place}`}
                </p>
            </div>

            <Badge tone={APPOINTMENT_STATUS_TONES[appointment.status] ?? 'warning'}>
                {t(APPOINTMENT_STATUS_KEYS[appointment.status] ?? 'portal.appointment_status.requested')}
            </Badge>
        </div>
    );
}
